import { Field, useForm } from 'react-final-form';
import _ from 'lodash';
import type { ValidatingFormComponent } from '@/formbuilder/formBuilder.types';
import { getFormFieldProps, getValidationFunction } from '@/formbuilder/formbuilder.utilities';
import { FormFieldWrapper } from '@/formbuilder/FormFieldWrapper';
import { ItemDescription } from '@/tools/itemProperties/ItemDescription';
import { AdvancedSection } from '@/core/AdvancedParameters.atom';
import { useTranslation } from 'react-i18next';

export interface DescriptionFormComponentProps extends ValidatingFormComponent<string> {
  component: 'DescriptionFormComponent';
  customErrorText?: string;
  extraClassNames?: string;
  wrapperClassNames?: string;
  canWrite: boolean;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  collapseLabel?: string;
}

export const DescriptionFormComponent: React.FunctionComponent<DescriptionFormComponentProps> = (props) => {
  const {
    name,
    testId = 'description',
    required,
    extraClassNames = '',
    wrapperClassNames,
    extendValidation,
    validation,
    canWrite,
    customErrorText,
    collapsible = false,
    defaultCollapsed = true,
    collapseLabel,
  } = props;
  const { t } = useTranslation();

  const formState = useForm().getState();
  const showError =
    _.has(formState.errors, name) &&
    formState.hasValidationErrors &&
    ((formState.modified && formState.modified[name]) || formState.submitFailed);

  const defaultValidation = (value: string) => (required && !value) as boolean;
  const appliedValidation = getValidationFunction(defaultValidation, extendValidation, validation);

  const descriptionField = (
    <Field name={name} validate={appliedValidation}>
      {({ input, meta }) => {
        const { value, onChange } = getFormFieldProps(formState, input, meta, props);
        return (
          <ItemDescription
            canWrite={() => canWrite}
            originalDesc={value}
            updateDescription={onChange}
            isWorkbookLocked={false}
          />
        );
      }}
    </Field>
  );

  const content = collapsible ? (
    <AdvancedSection collapsed={defaultCollapsed} alternateName={collapseLabel} t={t}>
      {descriptionField}
    </AdvancedSection>
  ) : (
    descriptionField
  );

  return (
    <FormFieldWrapper
      testId={testId}
      showError={showError}
      customErrorText={customErrorText}
      wrapperClassNames={wrapperClassNames}
      extraClassNames={extraClassNames}>
      {content}
    </FormFieldWrapper>
  );
};
